import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { EmployeePermissions } from '../../types/employee.types';
import {
  permissionsService,
  SystemPermission,
  PermissionsByModule,
  PermissionsByMenu
} from '@/services/permissions.service';

interface SystemPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  permissions: EmployeePermissions;
  onPermissionChange: (permission: keyof EmployeePermissions, checked: boolean) => void;
  onSave: () => void;
  employeeId?: number; // Optional for viewing actual granted permissions
}

export const SystemPermissionsModal: React.FC<SystemPermissionsModalProps> = ({
  isOpen,
  onClose,
  permissions,
  onPermissionChange,
  onSave,
  employeeId
}) => {
  const [systemPermissions, setSystemPermissions] = useState<PermissionsByMenu>({});
  const [menus, setMenus] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actualEmployeePermissions, setActualEmployeePermissions] = useState<Record<string, boolean>>({});
  const [loadingEmployeePermissions, setLoadingEmployeePermissions] = useState(false);

  // Legacy fallback permissions for backward compatibility
  const legacyPermissions = [
    { key: 'pos' as keyof EmployeePermissions, label: 'Punto de Venta', description: 'Acceso al sistema de ventas', module: 'pos' },
    { key: 'inventory' as keyof EmployeePermissions, label: 'Inventario', description: 'Gestión de stock y productos', module: 'inventory' },
    { key: 'reports' as keyof EmployeePermissions, label: 'Reportes', description: 'Visualización de reportes y análisis', module: 'reports' },
    { key: 'employees' as keyof EmployeePermissions, label: 'Empleados', description: 'Gestión de personal', module: 'employees' },
    { key: 'cashRegister' as keyof EmployeePermissions, label: 'Caja', description: 'Operaciones de caja registradora', module: 'cash_register' },
    { key: 'products' as keyof EmployeePermissions, label: 'Productos', description: 'Gestión del catálogo de productos', module: 'products' },
    { key: 'settings' as keyof EmployeePermissions, label: 'Configuración', description: 'Configuración del sistema', module: 'settings' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadSystemPermissions();
      if (employeeId) {
        loadEmployeePermissions();
      }
    }
  }, [isOpen, employeeId]);

  const loadSystemPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsService.getSystemPermissions();
      console.log('🔍 System permissions response:', response);

      // Check if we have the new menu-based structure
      if (response && response.permissionsByMenu && response.menus) {
        setSystemPermissions(response.permissionsByMenu);
        setMenus(response.menus);
        console.log('✅ Successfully loaded system permissions from API (menu-based)');
      }
      // Fallback to module-based structure if menu is not available
      else if (response && response.permissionsByModule && response.modules) {
        // Convert module-based to menu-based structure for compatibility
        const menuBased: PermissionsByMenu = {};
        Object.keys(response.permissionsByModule).forEach(module => {
          const permissions = response.permissionsByModule[module];
          permissions.forEach(permission => {
            const menu = permission.menu || 'Otros';
            if (!menuBased[menu]) {
              menuBased[menu] = [];
            }
            menuBased[menu].push(permission);
          });
        });
        setSystemPermissions(menuBased);
        setMenus(Object.keys(menuBased));
        console.log('✅ Successfully loaded and converted system permissions from API (module-based)');
      } else {
        console.warn('⚠️ API response missing expected structure, using fallback');
        console.warn('Response structure:', response);
        setMenus(['Venta', 'Maestros', 'Administración']);
      }
    } catch (error) {
      console.error('❌ Error loading system permissions:', error);
      // Fallback to legacy permissions if API fails
      setMenus(['Venta', 'Maestros', 'Administración']);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeePermissions = async () => {
    if (!employeeId) return;

    try {
      setLoadingEmployeePermissions(true);
      console.log('🔍 Loading actual employee permissions for employee:', employeeId);

      const response = await permissionsService.getEmployeePermissions(employeeId);
      console.log('🔍 Employee permissions response:', response);
      console.log('🔍 Response structure:', JSON.stringify(response, null, 2));

      // Convert the employee's actual permissions to a lookup object
      const actualPermissions: Record<string, boolean> = {};

      // Try different response structures
      let systemPermissions = response?.systemPermissions;
      if (!systemPermissions && response?.data?.systemPermissions) {
        systemPermissions = response.data.systemPermissions;
      }

      console.log('🔍 System permissions found:', systemPermissions);

      if (systemPermissions && Array.isArray(systemPermissions)) {
        systemPermissions.forEach((permission: any) => {
          console.log('🔍 Processing permission:', permission);
          const legacyKey = permission.module === 'cash_register' ? 'cashRegister' : permission.module;
          actualPermissions[legacyKey] = true;
          console.log(`✅ Set permission ${legacyKey} = true`);
        });
      } else {
        console.warn('⚠️ No system permissions found in response');
      }

      console.log('📋 Final processed employee permissions:', actualPermissions);
      setActualEmployeePermissions(actualPermissions);

    } catch (error) {
      console.error('❌ Error loading employee permissions:', error);
      console.error('❌ Error details:', error);
      setActualEmployeePermissions({});
    } finally {
      setLoadingEmployeePermissions(false);
    }
  };

  const handleSave = async () => {
    console.log('🔍 handleSave called with employeeId:', employeeId);
    console.log('🔍 Current permissions state:', permissions);

    if (!employeeId) {
      console.warn('❌ No employeeId provided, cannot save permissions');
      onSave();
      onClose();
      return;
    }

    try {
      setSaving(true);

      // Get current employee permissions
      console.log('🔍 Getting current employee permissions...');
      const currentPermissions = await permissionsService.getEmployeePermissions(employeeId);
      console.log('🔍 Current permissions response:', currentPermissions);

      const currentPermissionIds = currentPermissions?.systemPermissions?.map(p => p.id) || [];

      // Collect all selected permission IDs
      const selectedPermissionIds: number[] = [];
      const allAvailablePermissionIds: number[] = [];

      // Iterate through all system permissions to find which ones are selected
      // Use actualEmployeePermissions (local state) when editing an existing employee
      // This reflects the current UI state after user interactions
      Object.values(systemPermissions).flat().forEach(permission => {
        allAvailablePermissionIds.push(permission.id);
        const legacyKey = permission.module === 'cash_register' ? 'cashRegister' : permission.module;

        // Use local state for existing employees, parent state for new employees
        const isChecked = employeeId
          ? (actualEmployeePermissions[legacyKey] || false)
          : (permissions[legacyKey as keyof EmployeePermissions] as boolean);

        if (isChecked) {
          selectedPermissionIds.push(permission.id);
        }
      });

      console.log('🔄 Saving permissions for employee:', employeeId);
      console.log('📋 Current permission IDs:', currentPermissionIds);
      console.log('📋 Selected permission IDs:', selectedPermissionIds);

      // Find permissions to grant (selected but not currently granted)
      const permissionsToGrant = selectedPermissionIds.filter(id => !currentPermissionIds.includes(id));

      // Find permissions to revoke (currently granted but not selected)
      const permissionsToRevoke = currentPermissionIds.filter(id =>
        allAvailablePermissionIds.includes(id) && !selectedPermissionIds.includes(id)
      );

      console.log('➕ Permissions to grant:', permissionsToGrant);
      console.log('➖ Permissions to revoke:', permissionsToRevoke);

      // Grant new permissions
      if (permissionsToGrant.length > 0) {
        await permissionsService.grantEmployeePermissions(employeeId, {
          permissionIds: permissionsToGrant
        });
        console.log('✅ New permissions granted successfully');
      }

      // Revoke unselected permissions
      if (permissionsToRevoke.length > 0) {
        await permissionsService.revokeEmployeePermissions(employeeId, {
          permissionIds: permissionsToRevoke
        });
        console.log('✅ Permissions revoked successfully');
      }

      if (permissionsToGrant.length === 0 && permissionsToRevoke.length === 0) {
        console.log('ℹ️ No permission changes needed');
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('❌ Error saving permissions:', error);
      // TODO: Show error message to user
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Permisos del Sistema</DialogTitle>
          <DialogDescription>
            Selecciona los módulos del sistema a los que el empleado tendrá acceso
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {loading || loadingEmployeePermissions ? (
            <div className="flex justify-center py-8">
              <div className="text-sm text-gray-500">
                {loading ? 'Cargando permisos del sistema...' : 'Cargando permisos del empleado...'}
              </div>
            </div>
          ) : (
            <>
              {/* Show new system permissions grouped by menu if available */}
              {Object.keys(systemPermissions).length > 0 ? (
                <>
                  {menus.map((menu) => (
                    <div key={menu} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center mb-3">
                        <span className="w-3 h-3 bg-macartur-primary rounded-full mr-2"></span>
                        <h3 className="text-base font-semibold text-gray-800">{menu}</h3>
                      </div>
                      <div className="space-y-2 ml-5">
                        {systemPermissions[menu]?.map((permission) => {
                          // Map to legacy permission for compatibility
                          const legacyKey = permission.module === 'cash_register' ? 'cashRegister' : permission.module;

                          // Use local state for employee permissions (actualEmployeePermissions)
                          // This state is updated locally when user clicks
                          const isChecked = employeeId
                            ? (actualEmployeePermissions[legacyKey] || false)
                            : (permissions[legacyKey as keyof EmployeePermissions] as boolean || false);

                          return (
                            <div key={permission.id} className="flex items-start space-x-3">
                              <Checkbox
                                id={`perm-${permission.id}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  // Update local state for immediate UI feedback
                                  if (employeeId) {
                                    setActualEmployeePermissions(prev => ({
                                      ...prev,
                                      [legacyKey]: checked as boolean
                                    }));
                                  }
                                  // Also update parent state
                                  onPermissionChange(legacyKey as keyof EmployeePermissions, checked as boolean);
                                }}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <Label htmlFor={`perm-${permission.id}`} className="text-sm font-medium cursor-pointer">
                                  {permission.name}
                                </Label>
                                {permission.description && (
                                  <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                /* Fallback to legacy permissions */
                <>
                  {legacyPermissions.map((permission) => (
                    <div key={permission.key} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id={permission.key}
                        checked={permissions[permission.key] as boolean}
                        onCheckedChange={(checked) => onPermissionChange(permission.key, checked as boolean)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <Label htmlFor={permission.key} className="text-sm font-medium cursor-pointer">
                          {permission.label}
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-macartur-primary rounded-md hover:bg-macartur-accent1 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};